import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  apiGetAstrologers,
  apiGetSiteMeta,
  apiUpdateAstrologer,
  apiCreateAstrologer,
  apiDeleteAstrologer,
  apiMoveAstrologer,
  apiResetAll,
  apiUpdateSiteMeta,
} from "../api";

const AstrologersContext = createContext(null);

const DEFAULT_META = {
  heroTitle: "The Best Online Astrologers in India — 2025",
  heroSubtitle:
    "Find answers, insights, advice and peace of mind through Vedic, KP and Nadi astrology — from India's most trusted astrologers.",
  lastUpdated: "Last Updated: July 2025",
  disclosure: "Editorial Disclosure",
  tagline: "Editor's Choice — July 2025",
  brand: "topastrologerinindia.com",
};

export const AstrologersProvider = ({ children }) => {
  const [astrologers, setAstrologers] = useState([]);
  const [meta, setMetaState] = useState(DEFAULT_META);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, m] = await Promise.all([apiGetAstrologers(), apiGetSiteMeta()]);
      setAstrologers(list);
      setMetaState(m);
    } catch (e) {
      setError(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const updateAstrologer = async (id, patch) => {
    const updated = await apiUpdateAstrologer(id, patch);
    setAstrologers((prev) => prev.map((a) => (a.id === id ? updated : a)));
    return updated;
  };

  const addAstrologer = async (payload = {}) => {
    const created = await apiCreateAstrologer(payload);
    setAstrologers((prev) => [...prev, created]);
    return created.id;
  };

  const deleteAstrologer = async (id) => {
    await apiDeleteAstrologer(id);
    await refresh();
  };

  const reorderAstrologer = async (id, direction) => {
    const list = await apiMoveAstrologer(id, direction);
    setAstrologers(list);
  };

  const resetAll = async () => {
    const list = await apiResetAll();
    setAstrologers(list);
    const m = await apiGetSiteMeta();
    setMetaState(m);
  };

  const setMeta = async (newMeta) => {
    // Optimistic update + persist
    setMetaState(newMeta);
    try {
      const saved = await apiUpdateSiteMeta(newMeta);
      setMetaState(saved);
    } catch (e) {
      // Revert silently if save fails (still keep UI usable)
      console.error("Failed to save meta", e);
    }
  };

  return (
    <AstrologersContext.Provider
      value={{
        astrologers,
        meta,
        setMeta,
        loading,
        error,
        refresh,
        updateAstrologer,
        addAstrologer,
        deleteAstrologer,
        reorderAstrologer,
        resetAll,
      }}
    >
      {children}
    </AstrologersContext.Provider>
  );
};

export const useAstrologers = () => {
  const ctx = useContext(AstrologersContext);
  if (!ctx) throw new Error("useAstrologers must be used inside AstrologersProvider");
  return ctx;
};
